import { Injectable } from '@angular/core';
import * as protobuf from 'protobufjs';
import * as THREE from 'three';

export interface TerrainLoadResult {
  terrains: THREE.Group;
  offset: { x: number; y: number; z: number };
  siteId: number;
  blastId: number;
  bounds?: { min: THREE.Vector3; max: THREE.Vector3 };
}

export interface TerrainMesh {
  mesh: THREE.Mesh;
  name: string;
  uuid: string;
  terrainType: string;
}

/** Point du message décodé : protobufjs met les champs absents à zéro (`defaults: true`). */
interface DecodedVec3 {
  x?: number;
  y?: number;
  z?: number;
}

/**
 * Ce que deriveOrigin lit du message Terrains décodé (`toObject`) : l'offset du payload et les
 * sommets de chaque maillage. Un payload mal formé donne des champs absents, pas une exception.
 */
interface DecodedTerrains {
  offset?: DecodedVec3 | null;
  terrains?: readonly ({ mesh?: { points?: readonly DecodedVec3[] | null } | null } | null)[] | null;
}

/** Couleur d'un matériau décodé (Color de common.proto). */
interface DecodedColor {
  r?: number;
  g?: number;
  b?: number;
  a?: number;
}

/**
 * Ce que le chargeur lit d'un matériau du message décodé (Material de terrain.proto). Les octets
 * de la texture arrivent en base64 (`bytes: String`).
 */
interface DecodedMaterial {
  id?: number;
  texture?: { format?: string; imageData?: string | Uint8Array | null } | null;
  diffuse?: DecodedColor | null;
  emission?: DecodedColor | null;
  specular?: DecodedColor | null;
  shininess?: number;
}

@Injectable({
  providedIn: 'root'
})
export class TerrainLoaderService {
  private root: protobuf.Root | null = null;
  private TerrainsType: protobuf.Type | null = null;

  constructor() {
    this.initializeProtobuf();
  }

  /**
   * Initialize protobuf definitions
   */
  private async initializeProtobuf(): Promise<void> {
    try {
      // Load proto files
      this.root = await protobuf.load([
        'assets/proto/common.proto',
        'assets/proto/terrain.proto'
      ]);

      // Get Terrains message type
      this.TerrainsType = this.root.lookupType('epc.vertex.Terrains');
    } catch (error) {
      console.error('Failed to load protobuf definitions:', error);
      throw error;
    }
  }

  /**
   * Ensure protobuf is initialized
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.TerrainsType) {
      await this.initializeProtobuf();
    }
  }

  /**
   * Load terrain from binary protobuf data
   * @param arrayBuffer Binary protobuf data from API
   * @param offsetPoint Optional offset to apply to vertices (for precision)
   * @param ownOrigin   Sans offsetPoint : retrancher aux sommets une origine tirée du terrain lui-même
   *                    (voir deriveOrigin), renvoyée dans `offset`. Sinon les sommets restent tels
   *                    quels et `offset` est celui du payload, que l'appelant retranche lui-même
   *                    (terrains de site).
   * @returns TerrainLoadResult with Three.js meshes
   */
  async loadTerrainFromBinary(arrayBuffer: ArrayBuffer, offsetPoint?: THREE.Vector3, ownOrigin = false): Promise<TerrainLoadResult> {
    await this.ensureInitialized();

    if (!this.TerrainsType) {
      throw new Error('Protobuf types not initialized');
    }

    try {
      // Decode binary data
      const uint8Array = new Uint8Array(arrayBuffer);
      const message = this.TerrainsType.decode(uint8Array);
      const terrains = this.TerrainsType.toObject(message, {
        longs: Number,
        enums: String,
        bytes: String,
        defaults: true,
        arrays: true,
        objects: true,
        oneofs: true
      });

      // Create Three.js group for all terrains
      const terrainsGroup = new THREE.Group();
      terrainsGroup.name = 'Terrains';

      // Process each terrain
      const terrainMeshes: TerrainMesh[] = [];

      // Origine retranchée aux sommets : imposée, tirée du terrain sur demande, ou aucune.
      const origin = offsetPoint ?? (ownOrigin ? this.deriveOrigin(terrains) : undefined);

      if (terrains.terrains && Array.isArray(terrains.terrains)) {
        terrains.terrains.forEach((terrain: unknown, protoIndex: number) => {
          const terrainMesh = this.createTerrainMesh(terrain, origin);
          if (terrainMesh) {
            // Rang dans le payload stocké : un terrain illisible est sauté sans renuméroter les
            // suivants, et remplacer une version vise ce rang (voir BlastTerrain côté API).
            terrainMesh.mesh.userData['protoIndex'] = protoIndex;
            terrainsGroup.add(terrainMesh.mesh);
            terrainMeshes.push(terrainMesh);
          }
        });
      }

      // Calculate bounds
      const bounds = this.calculateBounds(terrainsGroup);

      return {
        terrains: terrainsGroup,
        // L'origine réellement retranchée aux sommets, que l'appelant doit adopter pour
        // positionner caméra et contrôles sur le même repère que le maillage ; sans elle, l'offset
        // du payload, à retrancher par l'appelant.
        offset: origin ? { x: origin.x, y: origin.y, z: origin.z } : terrains.offset || { x: 0, y: 0, z: 0 },
        siteId: terrains.siteId || 0,
        blastId: terrains.blastId || 0,
        bounds
      };
    } catch (error) {
      console.error('Failed to load terrain from binary:', error);
      throw error;
    }
  }

  /**
   * Origine à retrancher aux sommets quand l'appelant n'en impose pas — cas d'un tir sans trou,
   * où aucune géométrie ne fixe déjà le repère de la scène.
   *
   * Les sommets sont stockés en coordonnées projetées absolues, de l'ordre du million, et
   * finissent dans un Float32Array. Les y laisser ne coûte pas qu'une perte de résolution : la
   * caméra se place elle aussi au million, et le shader retranche alors deux grands nombres
   * flottants l'un à l'autre. Il ne reste presque plus de chiffres significatifs, et les sommets
   * sautent d'un pas visible dès que la caméra bouge — d'autant plus nettement en zoomant, où ce
   * pas occupe plus de pixels.
   *
   * L'offset du payload serait le repère naturel, mais il vaut zéro sur tout ce qu'écrit l'import
   * OBJ. On se rabat donc sur le premier sommet rencontré, qui suffit à ramener la géométrie
   * près de l'origine.
   */
  private deriveOrigin(terrains: DecodedTerrains): THREE.Vector3 {
    const payloadOffset = terrains.offset;
    if (payloadOffset && (payloadOffset.x || payloadOffset.y || payloadOffset.z)) {
      return new THREE.Vector3(payloadOffset.x ?? 0, payloadOffset.y ?? 0, payloadOffset.z ?? 0);
    }

    const firstVertex = (terrains.terrains ?? [])
      .map(terrain => terrain?.mesh?.points?.[0])
      .find((point): point is DecodedVec3 => !!point);

    return firstVertex
      ? new THREE.Vector3(firstVertex.x ?? 0, firstVertex.y ?? 0, firstVertex.z ?? 0)
      : new THREE.Vector3();
  }

  /**
   * Create Three.js mesh from terrain data
   * @param terrain Terrain data from protobuf
   * @param offsetPoint Optional offset to apply to vertices (for precision)
   */
  private createTerrainMesh(terrain: any, offsetPoint?: THREE.Vector3): TerrainMesh | null {
    try {
      if (!terrain.mesh || !terrain.mesh.points || !terrain.mesh.indices) {
        console.warn('Terrain missing mesh data:', terrain.name);
        return null;
      }

      const meshData = terrain.mesh;

      // Create geometry
      const geometry = new THREE.BufferGeometry();

      // Convert Vec3d points to Float32Array with offset applied (for precision)
      const vertices = this.convertVec3dArrayToFloat32(meshData.points, offsetPoint);
      geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));

      // Add normals if available
      if (meshData.normals && meshData.normals.length > 0) {
        const normals = this.convertVec3ArrayToFloat32(meshData.normals);
        geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
      } else {
        geometry.computeVertexNormals();
      }

      const protoMaterials: DecodedMaterial[] = terrain.materials ?? [];

      // Add colors: use proto colors if available, otherwise altitude gradient — dès qu'un
      // matériau sans texture en a besoin, sans quoi ses faces sortiraient noires.
      if (meshData.colors && meshData.colors.length > 0) {
        const colors = this.convertVec3ArrayToFloat32(meshData.colors);
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      } else if (protoMaterials.length === 0 || protoMaterials.some(m => !this.hasTexture(m))) {
        const colors = this.createAltitudeGradientColors(meshData.points);
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      }

      // Add UV coordinates if available
      if (meshData.texCoords && meshData.texCoords.length > 0) {
        const uvs = this.convertVec2ArrayToFloat32(meshData.texCoords);
        geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
      }

      // Add indices
      const indices = new Uint32Array(meshData.indices);
      geometry.setIndex(new THREE.BufferAttribute(indices, 1));

      // Create material — use vertex colors if we have proto colors or no texture
      const hasColors = meshData.colors && meshData.colors.length > 0;
      const materialOf = (protoMaterial: DecodedMaterial | undefined) =>
        this.createTerrainMaterial(protoMaterial, hasColors || !this.hasTexture(protoMaterial));

      // Un OBJ en plusieurs matériaux : un groupe de faces et un matériau Three.js par matériau
      // du proto. Sans groupes, tout le maillage prendrait le premier matériau. Un seul matériau
      // portant des faces (les autres définis sans en avoir) : c'est lui, pas forcément le premier.
      const groups = this.faceGroups(meshData.nbFacesPerMaterialId, protoMaterials, indices.length);
      let material: THREE.Material | THREE.Material[];
      if (groups && groups.length > 1) {
        groups.forEach(group => geometry.addGroup(group.start, group.count, group.materialIndex));
        material = protoMaterials.map(materialOf);
      } else {
        material = materialOf(protoMaterials[groups?.[0].materialIndex ?? 0]);
      }

      // Create mesh
      const mesh = new THREE.Mesh(geometry, material);
      mesh.name = terrain.name || 'Terrain';
      mesh.userData = {
        uuid: terrain.uuid,
        terrainType: terrain.terrainType || 'GALLERY',
        epsgCode: terrain.epsgCode
      };

      return {
        mesh,
        name: terrain.name,
        uuid: terrain.uuid,
        terrainType: terrain.terrainType || 'GALLERY'
      };
    } catch (error) {
      console.error('Failed to create terrain mesh:', error);
      return null;
    }
  }

  /**
   * Create altitude-based gradient colors for terrain vertices
   * Blue (0x0066ff) for low altitudes
   * Violet (0x6600ff) for medium altitudes
   * Magenta (0xff00ff) for high altitudes
   */
  private createAltitudeGradientColors(points: any[]): Float32Array {
    // Find min and max Z values (altitude)
    let minZ = Infinity;
    let maxZ = -Infinity;

    for (const point of points) {
      if (point.z < minZ) minZ = point.z;
      if (point.z > maxZ) maxZ = point.z;
    }

    const colors = new Float32Array(points.length * 3);

    // Define gradient colors
    const lowColor = new THREE.Color(0x0066ff);   // Blue
    const midColor = new THREE.Color(0x6600ff);   // Violet
    const highColor = new THREE.Color(0xff00ff);  // Magenta

    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      // Normalize altitude to 0-1 range
      const t = (maxZ - minZ) > 0 ? (point.z - minZ) / (maxZ - minZ) : 0;

      let color: THREE.Color;

      if (t < 0.5) {
        // Interpolate between blue and violet (0 to 0.5)
        const localT = t * 2; // Map to 0-1
        color = new THREE.Color().lerpColors(lowColor, midColor, localT);
      } else {
        // Interpolate between violet and magenta (0.5 to 1)
        const localT = (t - 0.5) * 2; // Map to 0-1
        color = new THREE.Color().lerpColors(midColor, highColor, localT);
      }

      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }

    return colors;
  }

  /**
   * Groupes de faces de chaque matériau, d'après nbFacesPerMaterialId : les indices sont rangés
   * par matériau, dans l'ordre croissant des identifiants (mesh.proto). Malgré son nom, la map
   * compte des indices (3 par face), comme l'écrivent l'Expertir et l'export
   * (exportOBJModelsToProto) ; un compte en faces (somme × 3 = nombre d'indices) est aussi lu.
   *
   * Null quand la map est vide, ne couvre pas exactement les indices, ou cite un matériau absent :
   * le maillage garde alors son premier matériau, comme avant. Un seul groupe : un seul matériau
   * porte des faces.
   */
  private faceGroups(
    counts: Record<string, number> | null | undefined,
    materials: readonly DecodedMaterial[],
    indexCount: number,
  ): { start: number; count: number; materialIndex: number }[] | null {
    const entries = Object.entries(counts ?? {})
      .map(([id, count]) => ({ id: Number(id), count: Number(count) }))
      .filter(entry => entry.count > 0)
      .sort((a, b) => a.id - b.id);
    if (entries.length === 0) return null;

    const total = entries.reduce((sum, entry) => sum + entry.count, 0);
    const indicesPerCount = total === indexCount ? 1 : total * 3 === indexCount ? 3 : 0;
    if (!indicesPerCount) return null;

    const groups: { start: number; count: number; materialIndex: number }[] = [];
    let start = 0;
    for (const entry of entries) {
      const materialIndex = materials.findIndex(material => (material.id ?? 0) === entry.id);
      if (materialIndex < 0) return null;
      groups.push({ start, count: entry.count * indicesPerCount, materialIndex });
      start += entry.count * indicesPerCount;
    }
    return groups;
  }

  private hasTexture(material: DecodedMaterial | undefined): boolean {
    return !!material?.texture?.imageData;
  }

  /**
   * Create material for terrain
   * @param material Matériau du proto, ou undefined pour un terrain qui n'en a pas
   * @param useVertexColors Whether to use vertex colors for gradient
   */
  private createTerrainMaterial(material: DecodedMaterial | undefined, useVertexColors = false): THREE.MeshPhongMaterial {
    if (material) {
      // If material has a texture
      if (material.texture && material.texture.imageData) {
        try {
          const texture = this.createTextureFromImageData(
            material.texture.imageData,
            material.texture.format ?? ''
          );

          return new THREE.MeshPhongMaterial({
            map: texture,
            color: 0xffffff,
            side: THREE.DoubleSide,
            vertexColors: false,
            transparent: true,
            opacity: 0.2,
            depthWrite: false
          });
        } catch (error) {
          console.warn('Failed to load texture, using default material:', error);
        }
      }

      // If useVertexColors is true, use vertex colors instead of material colors
      if (useVertexColors) {
        return new THREE.MeshPhongMaterial({
          color: 0xffffff,
          side: THREE.DoubleSide,
          flatShading: false,
          vertexColors: true,
          transparent: true,
          opacity: 0.2,
          depthWrite: false
        });
      }

      // Use material colors
      return new THREE.MeshPhongMaterial({
        color: this.colorToThreeColor(material.diffuse),
        emissive: this.colorToThreeColor(material.emission),
        specular: this.colorToThreeColor(material.specular),
        shininess: material.shininess || 30,
        opacity: 0.2,
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: false
      });
    }

    // Default material if no materials defined
    return new THREE.MeshPhongMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      flatShading: false,
      vertexColors: useVertexColors,
      transparent: true,
      opacity: 0.2,
      depthWrite: false
    });
  }

  /**
   * Create Three.js texture from image bytes
   */
  private createTextureFromImageData(imageData: any, format: string): THREE.Texture {
    // If imageData is base64 string
    if (typeof imageData === 'string') {
      const img = new Image();
      img.src = `data:image/${format};base64,${imageData}`;
      const texture = new THREE.Texture(img);
      img.onload = () => {
        texture.needsUpdate = true;
      };
      return texture;
    }

    // If imageData is Uint8Array
    const blob = new Blob([imageData], { type: `image/${format}` });
    const url = URL.createObjectURL(blob);
    const texture = new THREE.TextureLoader().load(url);
    return texture;
  }

  /**
   * Convert protobuf Color to THREE.Color
   */
  private colorToThreeColor(color: any): THREE.Color {
    if (!color) {
      return new THREE.Color(0xffffff);
    }
    return new THREE.Color(color.r || 1, color.g || 1, color.b || 1);
  }

  /**
   * Convert array of Vec3d to Float32Array
   * @param vec3dArray Array of Vec3d objects
   * @param offsetPoint Optional offset to subtract from each vertex (for precision)
   */
  private convertVec3dArrayToFloat32(vec3dArray: any[], offsetPoint?: THREE.Vector3): Float32Array {
    const float32Array = new Float32Array(vec3dArray.length * 3);
    for (let i = 0; i < vec3dArray.length; i++) {
      const vec = vec3dArray[i];
      // Apply offset like in model-loader.service.ts to avoid double precision issues
      const x = vec.x - (offsetPoint ? offsetPoint.x : 0);
      const y = vec.y - (offsetPoint ? offsetPoint.y : 0);
      const z = vec.z - (offsetPoint ? offsetPoint.z : 0);

      float32Array[i * 3] = x;
      float32Array[i * 3 + 1] = y;
      float32Array[i * 3 + 2] = z;
    }
    return float32Array;
  }

  /**
   * Convert array of Vec3 to Float32Array
   */
  private convertVec3ArrayToFloat32(vec3Array: any[]): Float32Array {
    const float32Array = new Float32Array(vec3Array.length * 3);
    for (let i = 0; i < vec3Array.length; i++) {
      const vec = vec3Array[i];
      float32Array[i * 3] = vec.x;
      float32Array[i * 3 + 1] = vec.y;
      float32Array[i * 3 + 2] = vec.z;
    }
    return float32Array;
  }

  /**
   * Convert array of Vec2 to Float32Array
   */
  private convertVec2ArrayToFloat32(vec2Array: any[]): Float32Array {
    const float32Array = new Float32Array(vec2Array.length * 2);
    for (let i = 0; i < vec2Array.length; i++) {
      const vec = vec2Array[i];
      float32Array[i * 2] = vec.x;
      float32Array[i * 2 + 1] = vec.y;
    }
    return float32Array;
  }

  /**
   * Calculate bounding box for terrain group
   */
  private calculateBounds(group: THREE.Group): { min: THREE.Vector3; max: THREE.Vector3 } {
    const box = new THREE.Box3().setFromObject(group);
    return {
      min: box.min,
      max: box.max
    };
  }

  /**
   * Serialize Three.js Object3D models to protobuf binary format
   * @param models Array of Three.js Object3D models
   * @param offsetPoint The offset that was applied to vertices (to restore original coordinates)
   * @param blastId The blast ID
   * @param siteId The site ID (optional)
   * @param textureFiles Optional array of texture files to include
   * @returns ArrayBuffer containing protobuf binary data
   */
  async serializeTerrainsToBinary(
    models: THREE.Object3D[],
    offsetPoint: THREE.Vector3,
    blastId: number,
    siteId = 0,
    textureFiles: File[] = []
  ): Promise<ArrayBuffer> {
    await this.ensureInitialized();

    if (!this.TerrainsType) {
      throw new Error('Protobuf types not initialized');
    }

    const terrainsData: any = {
      uuid: this.generateUUID(),
      terrains: [],
      offset: { x: offsetPoint.x, y: offsetPoint.y, z: offsetPoint.z },
      siteId: siteId,
      blastId: blastId
    };

    // Load texture files into a map for quick lookup
    const textureDataMap = new Map<string, { data: Uint8Array; format: string }>();
    for (const file of textureFiles) {
      const arrayBuffer = await file.arrayBuffer();
      const format = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      textureDataMap.set(file.name.toLowerCase(), {
        data: new Uint8Array(arrayBuffer),
        format: format
      });
    }

    // Process each model
    for (const model of models) {
      const terrainData = await this.extractTerrainFromObject3D(model, offsetPoint, textureDataMap);
      if (terrainData) {
        terrainsData.terrains.push(terrainData);
      }
    }

    // Encode to protobuf binary
    const errMsg = this.TerrainsType.verify(terrainsData);
    if (errMsg) {
      console.warn('Protobuf verification warning:', errMsg);
    }

    const message = this.TerrainsType.create(terrainsData);
    const buffer = this.TerrainsType.encode(message).finish();

    return (buffer.buffer as ArrayBuffer).slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength);
  }

  /**
   * Extract terrain data from a Three.js Object3D
   */
  private async extractTerrainFromObject3D(
    object: THREE.Object3D,
    offsetPoint: THREE.Vector3,
    textureDataMap: Map<string, { data: Uint8Array; format: string }>
  ): Promise<any | null> {
    const meshes: THREE.Mesh[] = [];

    // Collect all meshes from the object
    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        meshes.push(child);
      }
    });

    if (meshes.length === 0) {
      return null;
    }

    // Merge all meshes into one terrain
    const allPoints: { x: number; y: number; z: number }[] = [];
    const allNormals: { x: number; y: number; z: number }[] = [];
    const allIndices: number[] = [];
    const allTexCoords: { x: number; y: number }[] = [];
    /** Nombre d'indices (3 par face) de chaque matériau (clé : son id), dans l'ordre où ses faces sont écrites. */
    const nbFacesPerMaterialId: Record<number, number> = {};
    const materials: any[] = [];
    let indexOffset = 0;
    let materialId = 0;
    let hasTexture = false;

    for (const mesh of meshes) {
      const geometry = mesh.geometry;
      const positionAttr = geometry.getAttribute('position');
      const normalAttr = geometry.getAttribute('normal');
      const uvAttr = geometry.getAttribute('uv');
      const indexAttr = geometry.index;

      if (!positionAttr) continue;

      // Get world matrix to transform vertices
      mesh.updateMatrixWorld(true);
      const worldMatrix = mesh.matrixWorld;

      // Extract vertices and restore original coordinates by adding offset back
      for (let i = 0; i < positionAttr.count; i++) {
        const vertex = new THREE.Vector3(
          positionAttr.getX(i),
          positionAttr.getY(i),
          positionAttr.getZ(i)
        );
        vertex.applyMatrix4(worldMatrix);

        allPoints.push({
          x: vertex.x + offsetPoint.x,
          y: vertex.y + offsetPoint.y,
          z: vertex.z + offsetPoint.z
        });
      }

      // Extract normals
      if (normalAttr) {
        const normalMatrix = new THREE.Matrix3().getNormalMatrix(worldMatrix);
        for (let i = 0; i < normalAttr.count; i++) {
          const normal = new THREE.Vector3(
            normalAttr.getX(i),
            normalAttr.getY(i),
            normalAttr.getZ(i)
          );
          normal.applyMatrix3(normalMatrix).normalize();
          allNormals.push({ x: normal.x, y: normal.y, z: normal.z });
        }
      } else {
        // If normals are not provided, generate default normals (up-vector)
        for (let i = 0; i < positionAttr.count; i++) {
          allNormals.push({ x: 0, y: 1, z: 0 });
        }
      }

      // Extract UVs
      if (uvAttr) {
        for (let i = 0; i < uvAttr.count; i++) {
          allTexCoords.push({ x: uvAttr.getX(i), y: uvAttr.getY(i) });
        }
      } else {
        // If no UV attribute, add default UVs to keep counts aligned
        for (let i = 0; i < positionAttr.count; i++) {
          allTexCoords.push({ x: 0, y: 0 });
        }
      }

      // Faces et matériaux. L'Expertir attribue chaque face à son matériau grâce à
      // nbFacesPerMaterialId (mesh.proto) : les faces doivent être regroupées par matériau, dans
      // l'ordre croissant des identifiants. Malgré son nom, la map compte des indices (3 par face),
      // c'est ce qu'écrit l'Expertir lui-même : sa somme vaut indices.length. Un OBJ en plusieurs
      // parties (usemtl) donne un maillage à plusieurs groupes, où un même matériau peut revenir dans
      // des groupes non contigus : on rassemble ses faces avant de passer au suivant.
      const meshMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      const vertexIndices = indexAttr
        ? Array.from({ length: indexAttr.count }, (_unused, i) => indexAttr.getX(i))
        : Array.from({ length: positionAttr.count }, (_unused, i) => i);
      const groups = geometry.groups.length
        ? geometry.groups
        : [{ start: 0, count: vertexIndices.length, materialIndex: 0 }];

      for (let materialIndex = 0; materialIndex < meshMaterials.length; materialIndex++) {
        const id = materialId++;
        let faceCount = 0;
        for (const group of groups) {
          if ((group.materialIndex ?? 0) !== materialIndex) continue;
          // Triangles entiers seulement ; un groupe sans fin explicite (count Infinity) va au bout.
          const end = Math.min(group.start + group.count, vertexIndices.length);
          for (let i = group.start; i + 2 < end; i += 3) {
            allIndices.push(vertexIndices[i] + indexOffset, vertexIndices[i + 1] + indexOffset, vertexIndices[i + 2] + indexOffset);
            faceCount++;
          }
        }
        if (faceCount > 0) {
          nbFacesPerMaterialId[id] = faceCount * 3;
        }

        const materialData = await this.extractMaterialData(meshMaterials[materialIndex], textureDataMap, id);
        if (materialData) {
          materials.push(materialData);
          if (materialData.texture) {
            hasTexture = true;
          }
        }
      }

      indexOffset += positionAttr.count;
    }

    // Add gradient colors if no texture
    let allColors: { x: number; y: number; z: number }[] | undefined;
    if (!hasTexture && allPoints.length > 0) {
      allColors = this.createAltitudeGradientColorsForExport(allPoints);
    }

    return {
      uuid: this.generateUUID(),
      name: object.name || 'Imported Terrain',
      mesh: {
        points: allPoints,
        normals: allNormals,
        colors: allColors,
        indices: allIndices,
        nbFacesPerMaterialId,
        texCoords: allTexCoords.length > 0 ? allTexCoords : undefined
      },
      materials: materials.length > 0 ? materials : undefined,
      terrainType: 'GALLERY'
    };
  }

  /**
   * Create altitude-based gradient colors for export (Vec3 format)
   * Blue (0x0066ff) for low altitudes
   * Violet (0x6600ff) for medium altitudes
   * Magenta (0xff00ff) for high altitudes
   */
  private createAltitudeGradientColorsForExport(points: { x: number; y: number; z: number }[]): { x: number; y: number; z: number }[] {
    // Find min and max Z values (altitude)
    let minZ = Infinity;
    let maxZ = -Infinity;

    for (const point of points) {
      if (point.z < minZ) minZ = point.z;
      if (point.z > maxZ) maxZ = point.z;
    }

    const colors: { x: number; y: number; z: number }[] = [];

    // Define gradient colors
    const lowColor = new THREE.Color(0x0066ff);   // Blue
    const midColor = new THREE.Color(0x6600ff);   // Violet
    const highColor = new THREE.Color(0xff00ff);  // Magenta

    for (const point of points) {
      // Normalize altitude to 0-1 range
      const t = (maxZ - minZ) > 0 ? (point.z - minZ) / (maxZ - minZ) : 0;

      let color: THREE.Color;

      if (t < 0.5) {
        // Interpolate between blue and violet (0 to 0.5)
        const localT = t * 2;
        color = new THREE.Color().lerpColors(lowColor, midColor, localT);
      } else {
        // Interpolate between violet and magenta (0.5 to 1)
        const localT = (t - 0.5) * 2;
        color = new THREE.Color().lerpColors(midColor, highColor, localT);
      }

      colors.push({ x: color.r, y: color.g, z: color.b });
    }

    return colors;
  }

  /**
   * Extract material data including texture
   */
  private async extractMaterialData(
    material: THREE.Material,
    textureDataMap: Map<string, { data: Uint8Array; format: string }>,
    id: number
  ): Promise<any | null> {
    const materialData: any = {
      id: id,
      name: material.name || `Material_${id}`,
      shininess: 30,
      dissolve: 1.0
    };

    // Check if it's a material with color properties
    if (material instanceof THREE.MeshPhongMaterial ||
        material instanceof THREE.MeshStandardMaterial ||
        material instanceof THREE.MeshBasicMaterial) {

      const color = (material as any).color;
      if (color) {
        materialData.diffuse = { r: color.r, g: color.g, b: color.b, a: 1.0 };
      }

      if ((material as any).shininess !== undefined) {
        materialData.shininess = (material as any).shininess;
      }

      if (material.opacity !== undefined) {
        materialData.dissolve = material.opacity;
      }

      // Extract texture
      const map = (material as any).map;
      if (map && map instanceof THREE.Texture) {
        const textureData = await this.extractTextureData(map, textureDataMap);
        if (textureData) {
          materialData.texture = textureData;
        }
      }
    }

    return materialData;
  }

  /**
   * Extract texture data from Three.js Texture
   */
  private async extractTextureData(
    texture: THREE.Texture,
    textureDataMap: Map<string, { data: Uint8Array; format: string }>
  ): Promise<any | null> {
    // Try to get texture from the provided files map first
    if (texture.name) {
      const textureName = texture.name.toLowerCase();
      const textureFile = textureDataMap.get(textureName);
      if (textureFile) {
        return {
          format: textureFile.format,
          imageData: textureFile.data
        };
      }
    }

    // Try to extract from texture source URL
    if (texture.source && texture.source.data) {
      const source = texture.source.data;

      // If it's an HTMLImageElement, try to get it from the map by src filename
      if (source instanceof HTMLImageElement && source.src) {
        const srcParts = source.src.split('/');
        const filename = srcParts[srcParts.length - 1].split('?')[0].toLowerCase();

        const textureFile = textureDataMap.get(filename);
        if (textureFile) {
          return {
            format: textureFile.format,
            imageData: textureFile.data
          };
        }

        // Try to extract from canvas if image is loaded
        if (source.complete && source.naturalWidth > 0) {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = source.naturalWidth;
            canvas.height = source.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(source, 0, 0);
              const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
              const base64Data = dataUrl.split(',')[1];
              const binaryString = atob(base64Data);
              const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
              return {
                format: 'jpg',
                imageData: bytes
              };
            }
          } catch (e: unknown) {
            if (e instanceof DOMException && e.name === 'SecurityError') {
              console.warn('Failed to extract texture due to CORS restrictions:', e);
            } else {
              console.warn('Failed to extract texture from canvas:', e);
            }
          }
        }
      }
    }

    // No matching texture found - skip texture assignment to avoid incorrect mappings
    if (textureDataMap.size > 0) {
      console.warn('No matching texture found for material; skipping texture assignment.');
    }

    return null;
  }

  /**
   * Generate a UUID using cryptographically secure random values
   */
  private generateUUID(): string {
    return crypto.randomUUID();
  }
}
